/**
 * Single source of truth for the price-currency defaults used by the vendor
 * item form.
 *
 * A product carries two independent currency fields:
 *  - `seller_share_currency`      — the denomination of the number the seller
 *                                    types. A unit of account only.
 *  - `seller_settlement_currency` — the ERC-20 token the buyer is actually
 *                                    charged and the seller is actually paid.
 *
 * Only the settlement currency decides the payment token (see
 * `useCheckoutPurchase.getPaymentTokenId`). Every buyer-facing normalizer
 * treats a *missing* settlement currency as UVM, so the edit form must seed the
 * same value — seeding USDT would silently switch the charged token the next
 * time the seller saves an untouched product.
 */

export const DEFAULT_SHARE_CURRENCY = "UVM" as const;
export const DEFAULT_SETTLEMENT_CURRENCY = "UVM" as const;

export type ShareCurrency = "IRT" | "UVM" | "USDT";
export type SettlementCurrency = "UVM" | "USDT";

const SHARE_CURRENCIES: readonly ShareCurrency[] = ["IRT", "UVM", "USDT"];
const SETTLEMENT_CURRENCIES: readonly SettlementCurrency[] = ["UVM", "USDT"];

/** Price shape as it actually arrives. The generated `PriceDto` marks the
 *  currency fields required, but legacy rows predate them — so every field is
 *  optional here. Only the two currency fields are read; `bv` and
 *  `seller_share_unit` are accepted so callers can pass the whole price object. */
type PartialPrice = {
  bv?: string | number | null;
  seller_share_unit?: string | number | null;
  seller_share_currency?: string | null;
  seller_settlement_currency?: string | null;
};

export function resolvePriceCurrencyDefaults(
  price: PartialPrice | null | undefined,
): {
  seller_share_currency: ShareCurrency;
  seller_settlement_currency: SettlementCurrency;
} {
  const share = price?.seller_share_currency;
  const settlement = price?.seller_settlement_currency;

  return {
    seller_share_currency: SHARE_CURRENCIES.includes(share as ShareCurrency)
      ? (share as ShareCurrency)
      : DEFAULT_SHARE_CURRENCY,
    seller_settlement_currency: SETTLEMENT_CURRENCIES.includes(
      settlement as SettlementCurrency,
    )
      ? (settlement as SettlementCurrency)
      : DEFAULT_SETTLEMENT_CURRENCY,
  };
}
